/* ============================================================================
 * legalPacks.js — per-state contract legal content for the proposal appendix.
 *
 * The acceptance/signature page is state-agnostic. The optional LEGAL appendix
 * (Terms & Conditions + consumer-rights note + Notice of Cancellation) is
 * state-specific, so we key it by US state. Only states we have verified
 * wording for are populated; every other state resolves to `null`, and the UI
 * renders a clear "add your state's language" placeholder instead of showing
 * wrong or invented statutory text.
 *
 * Illinois is populated from K.O.'s real Johnson proposal document.
 * ========================================================================== */

const US_STATE_ABBR = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA',
  colorado: 'CO', connecticut: 'CT', delaware: 'DE', florida: 'FL',
  georgia: 'GA', hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN',
  iowa: 'IA', kansas: 'KS', kentucky: 'KY', louisiana: 'LA', maine: 'ME',
  maryland: 'MD', massachusetts: 'MA', michigan: 'MI', minnesota: 'MN',
  mississippi: 'MS', missouri: 'MO', montana: 'MT', nebraska: 'NE',
  nevada: 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM',
  'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', ohio: 'OH',
  oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI',
  'south carolina': 'SC', 'south dakota': 'SD', tennessee: 'TN', texas: 'TX',
  utah: 'UT', vermont: 'VT', virginia: 'VA', washington: 'WA',
  'west virginia': 'WV', wisconsin: 'WI', wyoming: 'WY',
};

/** Normalize a full state name or 2-letter code to an uppercase abbreviation. */
export const normalizeStateCode = (input) => {
  const raw = String(input || '').trim();
  if (!raw) return '';
  if (raw.length === 2) return raw.toUpperCase();
  return US_STATE_ABBR[raw.toLowerCase()] || raw.toUpperCase();
};

const ABBR_SET = new Set(Object.values(US_STATE_ABBR));

/**
 * Best-effort extraction of a US state from a free-text address line, since the
 * builder captures the project address as one string (e.g.
 * "123 Main St, Springfield, IL 62701"). Tries, in order: a 2-letter code
 * immediately before a ZIP, a full state name anywhere, then any standalone
 * valid 2-letter code. Returns '' when nothing recognizable is found.
 */
export const stateFromText = (text) => {
  const s = String(text || '').trim();
  if (!s) return '';
  // 1) "<STATE> <ZIP>" — the most reliable signal in a US address.
  const z = s.match(/\b([A-Za-z]{2})\s+\d{5}(?:-\d{4})?\b/);
  if (z && ABBR_SET.has(z[1].toUpperCase())) return z[1].toUpperCase();
  // 2) A full state name anywhere in the string.
  const lower = s.toLowerCase();
  for (const [name, code] of Object.entries(US_STATE_ABBR)) {
    const re = new RegExp(`\\b${name.replace(/\s+/g, '\\s+')}\\b`, 'i');
    if (re.test(lower)) return code;
  }
  // 3) Any standalone valid 2-letter code (last resort).
  const tokens = s.toUpperCase().match(/\b[A-Z]{2}\b/g) || [];
  for (const tk of tokens) if (ABBR_SET.has(tk)) return tk;
  return '';
};

/**
 * Resolve the legal-pack state for an estimate from every place the state might
 * live: an explicit override, the structured project-address column, the
 * free-text address line, then the company profile. Returns a normalized code.
 */
export const resolveLegalState = (estimate, company) =>
  normalizeStateCode(
    estimate?.legal_state ||
      estimate?.project_address_state ||
      stateFromText(estimate?.overview?.address) ||
      company?.state ||
      ''
  );

/* ---- Illinois -------------------------------------------------------------
 * Terms & Conditions, consumer-rights note, and the statutory Notice of
 * Cancellation, transcribed from K.O. Construction's Johnson proposal.
 * ------------------------------------------------------------------------- */
const IL_PACK = {
  code: 'IL',
  state: 'Illinois',
  terms: [
    {
      title: 'Payment and Suspension',
      body: 'Payments are due at the milestones stated in this proposal. Contractor may suspend work, reschedule crews or withhold closeout documents for non-payment. Owner is responsible for reasonable costs caused by payment delays, failed payments or remobilization.',
    },
    {
      title: 'Change Orders',
      body: 'Any work, material change, upgrade, quantity increase, design revision, added area or unforeseen condition outside the stated scope requires a written change order describing cost and schedule impact before the additional work proceeds, except emergency protective work reasonably necessary to prevent immediate damage or injury.',
    },
    {
      title: 'Material Orders and Substitutions',
      body: 'Custom and special-order materials may be ordered after the applicable cancellation period and required payment. Equivalent substitutions may be proposed when specified products are unavailable; owner approval is required for visible material/color changes.',
    },
    {
      title: 'Scheduling and Delays',
      body: 'Start and completion dates are estimates. Weather, inspections, engineering, material lead times, utility delays, change orders, owner decisions, unsafe conditions and events beyond Contractor control may extend the schedule without constituting breach.',
    },
    {
      title: 'Unforeseen Conditions',
      body: 'Pricing is based on visible conditions, available project information and standard soil assumptions. Hidden utilities, unsuitable soils, water, rock, structural irregularities, code-required corrections and other concealed conditions are outside the base price unless specifically included.',
    },
    {
      title: 'Access and Safety',
      body: 'Owner shall keep occupants, guests, children and pets away from active work areas and shall not direct or interfere with Contractor employees or subcontractors. Contractor may stop work when conditions are unsafe or access is unavailable.',
    },
    {
      title: 'Termination',
      body: 'Either party may terminate for a material breach after written notice and a reasonable opportunity to cure. Contractor may terminate or suspend immediately for non-payment, unsafe conditions or owner interference. Owner remains responsible for completed work, committed materials, restocking, demobilization and approved change orders.',
    },
    {
      title: 'Insurance and Risk of Loss',
      body: 'Contractor shall maintain insurance required by applicable law during performance. Owner is responsible for property/builders-risk coverage. Contractor is not responsible for loss or damage caused by acts of nature, theft, vandalism or other events outside Contractor control except to the extent caused by Contractor negligence.',
    },
    {
      title: 'Lien Documents',
      body: 'Contractor reserves rights available under the Illinois Mechanics Lien Act. Applicable sworn statements and lien waivers will be provided as appropriate with payments and project closeout.',
    },
    {
      title: 'Governing Agreement',
      body: 'This proposal, approved drawings, written change orders and incorporated terms constitute the entire agreement. Oral statements do not modify the agreement. Illinois law governs. If any provision is unenforceable, the remaining provisions remain effective.',
    },
  ],
  consumerNote: {
    title: 'Illinois Consumer Documents',
    body: 'Before execution, Owner will be provided the separate Illinois Attorney General pamphlet titled “Home Repair: Know Your Consumer Rights” and the required acknowledgment form. When the transaction is signed under circumstances that trigger the statutory in-home cancellation right, Contractor will provide oral notice and two completed copies of the Notice of Cancellation form.',
  },
  // Red banner on the acceptance page.
  cancellationBanner:
    'YOU, THE CONSUMER, MAY CANCEL THIS TRANSACTION AT ANY TIME PRIOR TO MIDNIGHT OF THE THIRD BUSINESS DAY AFTER THE DATE OF THIS TRANSACTION. SEE THE ATTACHED NOTICE OF CANCELLATION FORM FOR AN EXPLANATION OF THIS RIGHT.',
  cancellation: {
    title: 'Notice of Cancellation',
    intro:
      'Use only when the transaction qualifies for the Illinois three-business-day cancellation right.',
    paragraphs: [
      'YOU MAY CANCEL THIS TRANSACTION, WITHOUT ANY PENALTY OR OBLIGATION, WITHIN 3 BUSINESS DAYS FROM THE ABOVE DATE.',
      'IF YOU CANCEL, ANY PROPERTY TRADED IN, ANY PAYMENTS MADE BY YOU, AND ANY NEGOTIABLE INSTRUMENT EXECUTED BY YOU UNDER THE CONTRACT OR TRANSACTION WILL BE RETURNED WITHIN 10 BUSINESS DAYS FOLLOWING RECEIPT BY THE SELLER OF YOUR CANCELLATION NOTICE, AND ANY SECURITY INTEREST ARISING OUT OF THE TRANSACTION WILL BE CANCELLED.',
      'IF YOU CANCEL, YOU MUST MAKE AVAILABLE TO THE SELLER AT YOUR RESIDENCE IN SUBSTANTIALLY AS GOOD A CONDITION AS WHEN RECEIVED, ANY GOODS DELIVERED TO YOU UNDER THIS CONTRACT OR TRANSACTION, OR YOU MAY, IF YOU WISH, COMPLY WITH THE INSTRUCTIONS OF THE SELLER REGARDING THE RETURN SHIPMENT OF THE GOODS AT THE SELLER’S EXPENSE AND RISK.',
      'IF YOU MAKE THE GOODS AVAILABLE TO THE SELLER AND THE SELLER DOES NOT PICK THEM UP WITHIN 20 DAYS OF THE DATE OF YOUR NOTICE OF CANCELLATION, YOU MAY RETAIN OR DISPOSE OF THE GOODS WITHOUT ANY FURTHER OBLIGATION.',
      'IF YOU FAIL TO MAKE THE GOODS AVAILABLE TO THE SELLER, OR IF YOU AGREE TO RETURN THE GOODS TO THE SELLER AND FAIL TO DO SO, THEN YOU REMAIN LIABLE FOR PERFORMANCE OF ALL OBLIGATIONS UNDER THE CONTRACT.',
    ],
    // {seller} is filled with the company name at render time.
    howToCancel:
      'TO CANCEL THIS TRANSACTION, MAIL OR DELIVER A SIGNED AND DATED COPY OF THIS CANCELLATION NOTICE OR ANY OTHER WRITTEN NOTICE TO {seller}, NOT LATER THAN MIDNIGHT OF THE THIRD BUSINESS DAY AFTER THE DATE OF THIS TRANSACTION.',
    acknowledgment: 'I HEREBY CANCEL THIS TRANSACTION.',
  },
};

export const LEGAL_PACKS = {
  IL: IL_PACK,
};

/** Return the legal pack for a state (name or code), or null if not configured. */
export const getLegalPack = (stateInput) => {
  const code = normalizeStateCode(stateInput);
  return LEGAL_PACKS[code] || null;
};

/** List of states we currently have verified legal packs for (for the UI). */
export const CONFIGURED_LEGAL_STATES = Object.values(LEGAL_PACKS).map((p) => ({
  code: p.code,
  state: p.state,
}));
